import { capitalize } from '@general/utility';
import {
    ConnectionsStrings,
    TenantsSpacesStrings,
    ProductsStrings,
} from '@general/helpers/SavedSearchesQuestionStrings';

export const getSavedSearchTitle = savedSearch => {
    const { default: isDefault, title, primaryInterest } = savedSearch;

    if (isDefault && !title && primaryInterest) {
        return `DEFAULT - ${savedSearch.primaryInterest.toUpperCase()}`;
    }

    return title;
};

export const generateSavedSearchTitle = savedSearch => {
    const titleStrings = {
        tenants: values => {
            const purposeString = values.tenants?.purpose
                .map(purpose => TenantsSpacesStrings.purpose[`${purpose.toUpperCase()}`])
                .join(' ,');

            const tenantsTitleString = !purposeString ? 'Finding Tenants' : `Finding "${purposeString}" Tenants`;

            return tenantsTitleString;
        },
        space: values => {
            const purposeString = values.space?.purpose
                .map(purpose => TenantsSpacesStrings.purpose[`${purpose.toUpperCase()}`])
                .join(', ');

            const valuesTitleString = !purposeString ? 'Finding Space' : `Finding "${purposeString}" Space`;

            return valuesTitleString;
        },
        connections: values => {
            const connectionString = ConnectionsStrings.connectionType[values.connections.connectionType];

            const connectionsTitleString = !connectionString
                ? 'Finding Connections'
                : `Finding "${connectionString}" Connections`;

            return connectionsTitleString;
        },

        products: values => {
            const productTypesLength = values.products.productTypes?.length;
            let titleText = '';

            if (values.products?.areYou) {
                titleText = `${values.products?.areYou.toUpperCase()}` === 'LOOKING' ? 'Finding ' : 'Offering ';
            } else {
                titleText = 'Finding or offering ';
            }

            if (!productTypesLength) {
                titleText += ' products or services ';
                return titleText;
            }

            values.products.productTypes.forEach((productType, productTypeIndex) => {
                const productTypeText = ProductsStrings.productTypes[`${productType.toUpperCase()}`];

                titleText += productTypeText;
                if (productTypeIndex < productTypesLength - 1 && productTypesLength > 2) {
                    titleText += ', ';
                }

                if (productTypeIndex === productTypesLength - 2) {
                    if (productTypesLength === 2) {
                        titleText += ' ';
                    }

                    titleText += 'and ';
                }
            });

            return titleText;
        },
    };

    const locationString = values => {
        const location =
            values.businessLocations.length > 1 ? 'Multiple Locations' : values.businessLocations[0] ?? 'All Locations';

        return ` in ${location}`;
    };

    const titleCase = str => str.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());

    let titleString = titleStrings[savedSearch.primaryInterest.toLowerCase()](savedSearch);
    titleString += locationString(savedSearch);
    return titleCase(titleString);
};

/**
 * Pass the savedSearch object from an Algolia hit on saved_searchable_members
 * get an array of display ready strings for a Component's HTML
 *
 * @param object savedSearch from saved_searchable_members
 * @return array siteCriteriaList
 */
export const convertSavedSearchSpaceToList = savedSearch => {
    const siteCriteriaList = [];

    // Business Locations to Regions
    if (savedSearch.businessLocations.length > 0) {
        siteCriteriaList['Regions'] = savedSearch.businessLocations // eslint-disable-line dot-notation
            .map(businessLocation => capitalize(businessLocation))
            .join(', ');
    }

    // Purpose to Purpose of Space
    if (savedSearch.space.purpose.length > 0) {
        siteCriteriaList['Purpose Of Space'] = savedSearch.space.purpose
            .map(purpose => TenantsSpacesStrings.purpose[purpose.toUpperCase()])
            .join(', ');
    }

    // Type of Center to Type of Space
    if (savedSearch.space.typeOfCenter.length > 0) {
        siteCriteriaList['Type Of Space'] = savedSearch.space.typeOfCenter
            .map(type => TenantsSpacesStrings.typeOfCenter[type.toUpperCase()])
            .join(', ');
    }

    // Density to Population Density
    if (savedSearch.space.density.length > 0) {
        siteCriteriaList['Population Density'] = savedSearch.space.density
            .map(density => TenantsSpacesStrings.density[density.toUpperCase()])
            .join(', ');
    }

    // Type of Facility
    if (savedSearch.space.typeOfFacility.length > 0) {
        siteCriteriaList['Type Of Facility'] = savedSearch.space.typeOfFacility
            .map(type => TenantsSpacesStrings.typeOfFacility[type.toUpperCase()])
            .join(', ');
    }

    // Size to Size of Space
    if (savedSearch.space.size.length > 0) {
        siteCriteriaList['Size of Space'] = savedSearch.space.size
            .map(type => TenantsSpacesStrings.size[`_${type.toUpperCase()}`])
            .join(', ');
    }

    return siteCriteriaList;
};
